import { useContext, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { FileWithPath } from 'react-dropzone';
import { AppContext } from '@/contexts/app-context';
import { useGlobalQueries } from '@/hooks/use-global-queries';
import { useLocalization } from '@/hooks/use-localization';
import { FormValues, SubmitResult } from './types';

type UseFormSubmitOptions = {
    formId: string;
    submissionRoute: string;
    onBeforeSubmit?: (formValues: FormValues) => boolean | SubmitResult;
    onSuccess?: (submitResult: SubmitResult) => void;
    generalSubmitErrorMessage?: string;
    includeB2bConfirmed?: boolean;
    /**
     * Custom submission handler. When provided, replaces the default email path.
     * Receives form values, recaptcha token, and the pre-submit result.
     * Should resolve on success or throw on error.
     */
    onSubmit?: (formValues: FormValues, token: string, submitResult: SubmitResult) => Promise<void>;
};

export const useFormSubmit = ({
    formId,
    submissionRoute,
    onBeforeSubmit,
    onSuccess,
    generalSubmitErrorMessage,
    includeB2bConfirmed = false,
    onSubmit,
}: UseFormSubmitOptions) => {
    const { role, siteId, deviceType } = useContext(AppContext);
    const { executeRecaptcha } = useGoogleReCaptcha();
    const { getPermalink } = useGlobalQueries();
    const { localize } = useLocalization();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [finished, setFinished] = useState(false);
    const [submitResult, setSubmitResult] = useState<SubmitResult>({ confirmed: true });

    const handleSubmit = async (formValues: FormValues, files: FileWithPath[]) => {
        if (loading) {
            return;
        }

        if (!executeRecaptcha) {
            setError(localize('invalid-captcha'));
            return;
        }

        const token = await executeRecaptcha();
        if (!token) {
            setError(localize('invalid-captcha'));
            return;
        }

        // Run pre-submit validation if provided
        let result: SubmitResult = { confirmed: true };
        if (onBeforeSubmit) {
            const beforeResult = onBeforeSubmit(formValues);
            if (beforeResult === false) {
                return; // Prevent submission
            }
            if (typeof beforeResult === 'object') {
                result = beforeResult;
            }
        }
        setSubmitResult(result);

        const genericErrorMessage = generalSubmitErrorMessage || localize('error-generic') || 'There was an error. Please try again later.';

        // When a custom onSubmit handler is provided, use it instead of the default email path
        if (onSubmit) {
            setError('');
            setLoading(true);
            try {
                await onSubmit(formValues, token, result);
                setFinished(true);
                onSuccess?.(result);
            } catch {
                setError(genericErrorMessage);
            } finally {
                setLoading(false);
            }
            return;
        }

        const formData = new FormData();
        formData.set('permalink', getPermalink());
        formData.set('formId', formId || '');

        // Add confirmation status for B2B forms only
        if (includeB2bConfirmed && result.confirmed !== undefined) {
            formData.set('b2bConfirmed', String(result.confirmed));
        }

        Object.entries(formValues).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        if (files.length > 0) {
            files.forEach(file => {
                formData.append('files', file);
            });
        }

        setError('');
        setLoading(true);

        const response = await fetch(submissionRoute, {
            headers: {
                Accept: 'application/json',
                credentials: 'include',
                'x-role': role,
                'x-site-id': siteId,
                'x-device-type': deviceType,
                'x-recaptcha-token': JSON.stringify(token),
            },
            method: 'POST',
            body: formData,
        });

        setLoading(false);

        // Parse response body - handle non-JSON responses (e.g., HTML error pages)
        let data: unknown;
        try {
            data = await response.json();
        } catch {
            setError(genericErrorMessage);
            return;
        }

        if (!response.ok) {
            if (response.status === 498) {
                const errorData = data as { error?: string };
                setError(errorData?.error === 'INVALID_CAPTCHA_TOKEN' ? localize('invalid-captcha') : localize('invalid-token'));
            } else {
                setError(genericErrorMessage);
            }
            return;
        }

        const mailResults = Array.isArray(data) ? data : [];
        const hasSuccessfulDelivery = mailResults.some((r: { accepted?: string[] }) => r?.accepted && r.accepted.length > 0);

        if (!hasSuccessfulDelivery) {
            setError(genericErrorMessage);
            return;
        }

        setFinished(true);
        onSuccess?.(result);
    };

    return {
        loading,
        error,
        finished,
        submitResult,
        handleSubmit,
    };
};
